import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, provide, ref, watch } from 'vue';

import {
	GetTitleOffersDocument,
	GetTitleOffersQuery,
	GetTitleOffersQueryVariables,
} from '@/features/buybox/graphql/queries/GetTitleOffers.query';

import { ImpressionTrackingEvents } from '@/enums/events';
import { OfferLocation } from '@/helpers/offer-helper';
import {
	SnowplowClickoutContextGraphql,
	SnowplowContext,
	SnowplowTitleContextGraphql,
} from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { timestamp } from '@/helpers/date-helper';

import type { TitleOffers } from '@/interfaces/buybox';
import { OfferPresentationType } from '@/interfaces/titles';

import { ObjectType, Offer as OfferGraphql, Scoring, SraPlacement } from '@/@types/graphql-types';
import { MonetizationType, PresentationType, SraEventAction } from '@/@types/graphql-types';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';

import SponsoredRecommendation from '@/features/sponsored-recommendations/components/SponsoredRecommendation.vue';
import { useSendBidId } from '@/features/sponsored-recommendations/composables/useSendBidId';

import { allowedPromotionLanguages, usePromotion } from '@/helpers/composables/usePromotion';

import { getExcludedPackageShortNames } from '@/constants/package-exclusion';

import { CountriesToBeSorted, sortOffersByMonetizationPotential } from '@/features/buybox/helpers/buy-box-helpers';

import { useProvideClickoutPlacement } from '@/helpers/composables/useProvideClickoutPlacement';
import { useConstantStore, useLanguageStore, useRoutingStore, useUserStore } from '@/helpers/composables/useStores';
import { useI18n } from '@/helpers/composables/useI18n';
import { useQuery } from '@/helpers/composables/useApollo';
import { ClickoutPlacement } from '@/interfaces/snowplow/clickout-context';
import { NewBuyBoxView } from '@/stores/user.store';

import GridBuyBoxFilter from '@/features/buybox/components/grid/GridBuyBoxFilter.vue';
import OfferRow from '@/features/buybox/components/grid/OfferRow.vue';
import BuyBoxCountrySwitcher from '@/features/buybox/components/BuyBoxCountrySwitcher.vue';
import { qtvProviders, qtvProviderType } from '@/features/buybox/helpers/quality-tv-data';
import { AssertByTypename } from '@/helpers/graphql-model-helper';
import { useTitleBasedSteering } from '@/helpers/ad/useCrunchyrollPromotion';
import { useSingleQTV } from '@/features/buybox/composables/useSingleQTV';
import { useBackendTracking } from '@/helpers/composables/useBackendTracking';
import { useExperiment } from '@/libraries/experiment';
import GridRowLabel from '@/features/buybox/components/grid/GridRowLabel.vue';
import GridSwiperExp from '@/components/experiments/GridSwiperExp/GridSwiperExp.vue';
import { useJWTVCampaign } from '@/features/jwtv-titles-rental/composables/useJWTVCampaign';
import { useBingeAuPromo } from '@/helpers/composables/useBingeAUPromo';


export default /*#__PURE__*/_defineComponent({
  __name: 'GridBuyBox',
  props: {
    titleId: null,
    titleObjectType: null,
    titleObjectId: null,
    titleName: { default: '' },
    title: null,
    showDetails: { default: null },
    isTitleInWatchlist: { type: Boolean },
    titleScoring: { default: () => ({}) },
    seasonNumber: { default: null },
    episodeNumber: { default: null },
    originalReleaseYear: { default: undefined },
    sponsoredAd: { default: undefined },
    isBannerBuyBox: { type: Boolean },
    additionalContexts: { default: () => [] },
    buyBoxSwitchType: null,
    showFilter: { type: Boolean },
    inline: { type: Boolean },
    noHeader: { type: Boolean },
    compact: { type: Boolean },
    defaultPresentationType: null,
    isInModal: { type: Boolean },
    isAVODActive: { type: Boolean },
    isEpisodeBuybox: { type: Boolean },
    placement: { default: 'regular_buybox' },
    index: null
  },
  emits: ["onWatchlistToggle", "buyboxLoading"],
  setup(__props: any, { emit: emits }: { emit: ({
	(e: 'onWatchlistToggle', createdAt: string | null): void;
	(e: 'buyboxLoading', isLoading: boolean): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { titleId: string, titleObjectType: ObjectType, titleObjectId: number, titleName: string, title: TitleDetail, showDetails: {
			id: string;
			objectId: number;
			objectType: ObjectType;
			title: string;
		} | null, isTitleInWatchlist?: boolean, titleScoring: Scoring, seasonNumber: number | null, episodeNumber: number | null, originalReleaseYear: number | null, sponsoredAd: SponsoredAdFragment, isBannerBuyBox?: boolean, additionalContexts: SnowplowContext[], buyBoxSwitchType?: NewBuyBoxView, showFilter?: boolean, inline?: boolean, noHeader?: boolean, compact?: boolean, defaultPresentationType?: OfferPresentationType, isInModal?: boolean, isAVODActive?: boolean, isEpisodeBuybox?: boolean, placement: ClickoutPlacement, index?: number };

const OfferReporter = () => import('@/features/buybox/components/OfferReporter.vue');
const FastOfferRow = () => import('@/features/buybox/components/grid/FastOfferRow.vue');
const NoOfferRow = () => import('@/features/buybox/components/grid/NoOfferRow.vue');
const GridPromotionRow = () => import('@/features/buybox/components/grid/GridPromotionRow.vue');
const QualityTvRow = () => import('@/features/buybox/components/grid/QualityTvRow.vue');
const GridSQTV = () => import('@/features/buybox/components/grid/GridSQTV.vue');
const GridOfferRowRedesignExp = () =>
	import('@/components/experiments/GridBuyboxRedesignExps/components/GridOfferRowRedesignExp.vue');





const episodeBuyBoxPresentationType = ref<OfferPresentationType | null>(null);

const { titleProviderCount } = useConstantStore();
const { language, country } = useLanguageStore();
const { buyboxPresentationType, isPremium, isLoggedIn, setBuyboxPresentationType, isProUser } = useUserStore();
const { activeRoute } = useRoutingStore();
const { t } = useI18n();
const { trackSraEvent } = useBackendTracking();

const { hasActiveJwtvCampaign } = useJWTVCampaign();

const { sponsoredAd, isShadowCampaign } = useSendBidId({
	pageType: 'VIEW_BUYBOX',
	placement: SraPlacement.Buybox,
	jwEntityID: props.title.id,
	// `props.inline` is only indicator for shadow SR placements (excluding banner, Search page, Editorial pages buybox )
	isEnabled: computed(() => !props.inline),
});

useProvideClickoutPlacement(() => props.placement);

provide('isBannerBuyBox', () => props.isBannerBuyBox);
provide('titleSourceId', props.titleId);
provide('buyboxIndex', () => props.index);

const buyBoxActiveType = computed(() => {
	return (
		props.isEpisodeBuybox ? episodeBuyBoxPresentationType.value : buyboxPresentationType.value
	) as OfferPresentationType | null;
});

const presentationTypeGraphql = computed(() => {
	const presentationTypeMap: Partial<Record<OfferPresentationType, PresentationType[]>> = {
		[OfferPresentationType._4K]: [PresentationType['4K'], PresentationType.Bluray_4K],
		[OfferPresentationType.HD]: [PresentationType.Hd, PresentationType.Bluray],
		[OfferPresentationType.SD]: [PresentationType.Sd, PresentationType.Dvd],
		[OfferPresentationType.DVD]: [PresentationType.Dvd],
		[OfferPresentationType.BLURAY]: [PresentationType.Bluray, PresentationType.Bluray_4K],
		[OfferPresentationType.CANVAS]: [PresentationType.Canvas],
	};

	if (buyBoxActiveType.value == null) return null;
	return presentationTypeMap[buyBoxActiveType.value];
});

const queryVariables = computed(() => {
	//prettier-ignore
	const presentationTypes = (presentationTypeGraphql.value ?? [])?.length > 0 ? presentationTypeGraphql.value : undefined;
	const dvdPresentationTypes = presentationTypes
		? presentationTypes
		: [PresentationType.Dvd, PresentationType.Bluray, PresentationType.Bluray_4K];
	return {
		nodeId: props.titleId,
		country: country.value,
		language: language.value,
		filterBuy: {
			monetizationTypes: [MonetizationType.Buy],
			bestOnly: true,
			preAffiliate: true,
			presentationTypes,
		},
		filterFlatrate: {
			monetizationTypes: [
				MonetizationType.Flatrate,
				MonetizationType.FlatrateAndBuy,
				MonetizationType.Ads,
				MonetizationType.Free,
				MonetizationType.Cinema,
			],
			presentationTypes,
			bestOnly: true,
			preAffiliate: true,
		},
		filterRent: {
			monetizationTypes: [MonetizationType.Rent],
			presentationTypes,
			bestOnly: true,
			preAffiliate: true,
		},
		filterFree: {
			monetizationTypes: [MonetizationType.Ads, MonetizationType.Free],
			presentationTypes,
			bestOnly: true,
			preAffiliate: true,
		},
		filterDvdBluRay: {
			monetizationTypes: [MonetizationType.Rent, MonetizationType.Buy],
			presentationTypes: dvdPresentationTypes,
			preAffiliate: true,
		},
	};
});

const { loading, result, onResult } = useQuery<GetTitleOffersQuery, GetTitleOffersQueryVariables>(
	GetTitleOffersDocument,
	queryVariables
);
// Cached Results are used to avoid flickering when user changes the selected Tab as it triggers the query again and result set to null.
const cachedResults = ref<GetTitleOffersQuery | null>(null);
onResult(data => {
	if (!data.loading) {
		cachedResults.value = data.data ?? null;
	}
});

const offers = computed(() => {
	const node = (result.value ?? cachedResults.value)?.node;
	if (
		node &&
		(node.__typename === 'Episode' ||
			node.__typename === 'Movie' ||
			node.__typename === 'Season' ||
			node.__typename === 'Show')
	) {
		const { fast, free, flatrate, offerCount, maxOfferUpdatedAt, bundles } = node;
		const { rent, buy } = getRentAndBuyOffers(node);
		const res: TitleOffers = {
			stream: [],
			rent: rent,
			buy: buy,
			free: free,
			fast: fast,
			cinema: [],
			bundles: bundles as TitleOffers['bundles'],
			offerCount,
			maxOfferUpdatedAt,
		};
		flatrate.forEach(offer =>
			offer.monetizationType === MonetizationType.Cinema ? res.cinema.push(offer) : res.stream.push(offer)
		);
		return res;
	}

	return {
		stream: [],
		rent: [],
		buy: [],
		free: [],
		fast: [],
		cinema: [],
		bundles: [],
		offerCount: 0,
		maxOfferUpdatedAt: '',
	};
});

const { hardcodedShortName, hasTitleBasedSteering } = useTitleBasedSteering({ title: () => props.title });

const { inPromotionCatalogue, catalogueItem } = usePromotion({
	title: () => props.title,
	offers: () => {
		const { free = [], stream } = offers.value;
		return [...free, ...stream];
	},
	hardcodedShortName,
});

// Grid Swiper Exp
const { activeVariantWithControlGroup: expGridSwiperVariant } = useExperiment('EXP_GRID_SWIPER');
const expGridSwiperActive = computed(
	() => !props.inline && expGridSwiperVariant.value && expGridSwiperVariant.value !== 'control'
);

// Grid Swiper Exp

// Binge Promo AU SQTV

const { isBingeAuPromoTitle } = useBingeAuPromo({
	title: () => props.title,
});

//

// Grid Redesign Exps

const { activeVariantWithControlGroup: gridRedesignVariant } = useExperiment('EXP_GRID_REDESIGN_CLICK');

const renderGridRedesign = computed(
	() => !!gridRedesignVariant.value && gridRedesignVariant.value !== 'control' && !props.inline
);

const isFreeBuyBox = computed(() => {
	return buyBoxActiveType.value === OfferPresentationType.FREE;
});

const excludedPackages = computed(() => {
	const titleObjectId = props.title.__typename === 'Season' ? props.title?.show.id : props.title.id;
	return getExcludedPackageShortNames(isLoggedIn.value, titleObjectId, country.value, isProUser.value);
});

const filteredOffers = computed(() => {
	// Start with the full list of offers
	let _offers = { ...offers.value };

	// Filtering out TVOD providers we don't have a partnership with
	const { offerCount, maxOfferUpdatedAt, bundles, ...allOffers } = _offers;

	const filteredOffers = Object.fromEntries(
		Object.entries(allOffers).map(([key, value]) => [
			key,
			value.filter(offer => !excludedPackages.value.includes(offer.package.shortName)),
		])
	);

	_offers = {
		...filteredOffers,
		bundles,
		offerCount,
		maxOfferUpdatedAt,
	} as TitleOffers;

	if (!props.isBannerBuyBox) return _offers;

	// Return filtered offers
	return sortOffersByMonetizationPotential(_offers, country.value as CountriesToBeSorted);
});

// Tubi Test >> for US only, sort buybox offers to show Tubi First
const filteredOffersFree = computed(() => filteredOffers.value.free);

const filteredOffersSorted = computed(() => filteredOffers.value.stream);
// Tubi Test

/** Excludes Free. The title does not have stream, rent, buy, or any other offers. */
const hasNoNonFreeOffers = computed(() => {
	return (
		(['stream', 'rent', 'buy', 'fast', 'cinema'] as const).reduce(
			(acc, curr) => (acc += (filteredOffers.value?.[curr] ?? []).length),
			0
		) === 0
	);
});

const hasOnlyCinemaOffers = computed(() => {
	const cinemaOfferCount = filteredOffers.value.cinema.length;

	const hasNoNonCinemaOffers =
		(['stream', 'rent', 'buy', 'fast', 'free', 'fast', 'bundles'] as const).reduce(
			(acc, curr) => (acc += (filteredOffers.value?.[curr] ?? []).length),
			0
		) === 0;

	return cinemaOfferCount > 0 && hasNoNonCinemaOffers;
});

const showPromotionRow = computed(() => {
	const rawLanguage = language.value.split('-')[0];
	return props.title && !isPremium.value && isFreeBuyBox.value && allowedPromotionLanguages.includes(rawLanguage);
});

const titleContext = computed(() => {
	const titleObjectId = props.showDetails ? props.showDetails.objectId : props.titleObjectId;
	const titleObjectType = props.showDetails ? props.showDetails.objectType : props.titleObjectType;
	return new SnowplowTitleContextGraphql(
		titleObjectId,
		titleObjectType,
		props.seasonNumber,
		props.episodeNumber,
		null,
		props.index
	);
});

provide('titleContext', titleContext);

const hasNoOffersToShow = computed(() => {
	// this buybox filter is about Monetization, so here specifically we ask
	// if there are no "FREE" offers only
	if (isFreeBuyBox.value) {
		const hasNoFreeOffers = filteredOffers.value.free.length === 0;

		// if there is a promotion from a provider that does have an offer,
		// then technically the promotion counts as an "offer".
		if (showPromotionRow.value) {
			return !inPromotionCatalogue.value && hasNoFreeOffers;
		}

		return hasNoFreeOffers;
	}

	return hasNoNonFreeOffers.value || hasOnlyCinemaOffers.value;
});

const lastUpdated = computed(() => {
	return filteredOffers.value.maxOfferUpdatedAt;
});

const isTitleDetailPage = computed(() => {
	return activeRoute.value.name?.includes('app.detail');
});

const timestampText = computed(() => {
	const updated = timestamp(
		lastUpdated.value,
		{ year: 'numeric', month: 'long', day: 'numeric' },
		country.value,
		language.value
	);
	return t('WEBAPP_BUYBOX_UPDATED_TIMESTAMP', {
		services_total: titleProviderCount.value,
		date: updated.date,
		time: updated.time,
	});
});

const sponsoredRecommendationPackageId = computed(() => {
	return props.sponsoredAd?.campaign?.watchNowOffer.package.packageId;
});

const sponsoredRecommendationCohort = computed(() => {
	return props.sponsoredAd?.holdoutGroup ? 0 : 1;
});

const offerReporterTitleOffers = computed(() => {
	return [
		...filteredOffers.value.free,
		...filteredOffers.value.stream,
		...filteredOffers.value.rent,
		...filteredOffers.value.buy,
		...filteredOffers.value.cinema,
	];
});

const fastOffers = computed(() => {
	// Exclude Offers that are no longer active.
	return filteredOffers.value.fast.filter(el => new Date(el.availableToTime) > new Date());
});

const showFastRow = computed(() => {
	return isFreeBuyBox.value || buyBoxActiveType.value === null;
});

const trackingContexts = computed(() => (props.additionalContexts || []).concat([titleContext.value]));

const hideTVODQTV = computed(() => {
	return (
		catalogueItem.value?.placement === 'qtv' &&
		!['FREE', 'JWTV'].includes(qtvProviderType[catalogueItem.value.shortName]) &&
		isFreeBuyBox.value
	);
});

const promotionProviderShortName = computed(() => catalogueItem.value?.shortName);

const qualityTvActive = computed(() => {
	if (expGridSwiperActive.value) return false;
	if (gridRedesignVariant.value) return false;
	return (
		!isProUser.value &&
		!props.inline &&
		!hasTitleBasedSteering.value &&
		!props.isBannerBuyBox &&
		// either placement is overwritten by yo, or the programmatic decision checks
		catalogueItem.value &&
		// show QTV on old buybox when SQTV is active
		catalogueItem.value.placement === 'qtv' &&
		qtvProviders.includes(catalogueItem.value.shortName) &&
		// Itunes TVOD QTV is not a Free Trial, cannot be shown when filter is Free.
		!hideTVODQTV.value
	);
});

const isSqtvActive = computed(() => {
	if (expGridSwiperActive.value) return false;
	if (gridRedesignVariant.value) return false;
	if (isProUser.value || props.inline || hasTitleBasedSteering.value) return false;

	if (isBingeAuPromoTitle.value) return true;

	return (
		!props.title.offers.some(o => o.package.shortName === hardcodedShortName.value) &&
		// either placement is overwritten by yo, or the programmatic decision checks
		!!catalogueItem.value &&
		catalogueItem.value.placement === 'sqtv'
	);
});

const { isSQTVEmpty } = useSingleQTV(
	() => props.title,
	() => promotionProviderShortName.value || null,
	isSqtvActive
);

const showSQTV = computed(() => isSqtvActive.value && !isSQTVEmpty.value);

const freeTrialActive = computed(() => {
	if (expGridSwiperActive.value) return false;
	if (gridRedesignVariant.value) return true;
	if (!!catalogueItem.value?.placement) {
		if (isSqtvActive.value) return isSQTVEmpty.value;
		// additional exception: banner, search & guide page buyboxes always render FT
		return !!props.inline || catalogueItem.value?.placement === 'ft';
	}

	return !catalogueItem.value?.placement && !qualityTvActive.value && !showSQTV.value;
});

const promotionShortName = computed(() => {
	if (!!catalogueItem.value?.placement) {
		return catalogueItem.value.shortName;
	}

	return null;
});

// Grid Redesign Exps
const offerRows = computed(() => [
	{
		key: 'stream',
		class: 'stream',
		offers: isFreeBuyBox.value ? filteredOffersFree.value : filteredOffersSorted.value,
		bundles: isFreeBuyBox.value ? [] : filteredOffers.value.bundles,
		label: isFreeBuyBox.value ? 'WEBAPP_FREE' : 'WEBAPP_STREAM',
		show: true,
	},
	{
		key: 'rent',
		class: 'rent',
		offers: filteredOffers.value.rent,
		label: 'WEBAPP_RENT',
		show: !isFreeBuyBox.value,
	},
	{
		key: 'buy',
		class: 'buy',
		offers: filteredOffers.value.buy,
		label: 'WEBAPP_BUY',
		show: !isFreeBuyBox.value,
	},
	{
		key: 'cinema',
		class: 'cinema',
		offers: filteredOffers.value.cinema,
		label: 'WEBAPP_CINEMA',
		show: !isFreeBuyBox.value,
	},
	{
		key: 'fast',
		class: 'fast',
		offers: fastOffers.value,
		label: 'WEBAPP_FREE_TV',
		show: showFastRow.value,
	},
]);
// Grid Redesign Exps

function handleEpisodeFilterChange(presentation: OfferPresentationType | null) {
	setEpisodeBuyboxPresentationType(presentation);
}

function setEpisodeBuyboxPresentationType(presentationType: OfferPresentationType | null) {
	episodeBuyBoxPresentationType.value = presentationType;
}

function clickOut(payload: { buyboxOffer: OfferGraphql; isLeavingIconShown: boolean; offerLocation?: OfferLocation }) {
	const clickoutContext = SnowplowClickoutContextGraphql.fromProviderOffer(payload.buyboxOffer);

	if (
		!!sponsoredRecommendationPackageId.value &&
		payload.buyboxOffer.package.packageId === sponsoredRecommendationPackageId.value
	) {
		let contexts: SnowplowContext[] = [];
		if (payload.buyboxOffer) {
			contexts = trackingContexts.value;
			contexts.push(clickoutContext);
		}

		TrackingHelper.trackEvent(
			ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS,
			{
				action: 'buybox_clickout',
				label: `${props.titleId}_${sponsoredRecommendationPackageId.value}`,
				property: 'buybox',
				value: sponsoredRecommendationCohort.value,
			},
			contexts
		);

		trackSraEvent({
			action: SraEventAction.BuyboxClickout,
			sponsoredAd: props.sponsoredAd,
			jwEntityID: props.titleId,
		});
	}
}

function getRentAndBuyOffers(
	titleOffers: AssertByTypename<GetTitleOffersQuery['node'], 'Episode' | 'Movie' | 'Season' | 'Show'>
) {
	const isDigital = (offer: any) =>
		![PresentationType.Bluray, PresentationType.Dvd, PresentationType.Bluray_4K].includes(offer.presentationType);

	const dvdRent = titleOffers.dvdBluRay
		.filter(o => o.monetizationType === MonetizationType.Rent && !isDigital(o))
		.sort((a, b) => (a.retailPrice || '').localeCompare(b.retailPrice || '', undefined, { numeric: true }));

	const dvdBuy = titleOffers.dvdBluRay.filter(o => o.monetizationType === MonetizationType.Buy && !isDigital(o));

	return {
		rent: [...titleOffers.rent.filter(isDigital), ...dvdRent],
		buy: [...titleOffers.buy.filter(isDigital), ...dvdBuy],
	};
}

watch(loading, () => {
	if (loading.value) {
		emits('buyboxLoading', true);
	}
});

onMounted(() => {
	if (activeRoute.value?.query.monetization_types === 'free') {
		setBuyboxPresentationType({ presentationType: OfferPresentationType.FREE });
	}
});

return { __sfc: true,OfferReporter, FastOfferRow, NoOfferRow, GridPromotionRow, QualityTvRow, GridSQTV, GridOfferRowRedesignExp, props, emits, episodeBuyBoxPresentationType, titleProviderCount, language, country, buyboxPresentationType, isPremium, isLoggedIn, setBuyboxPresentationType, isProUser, activeRoute, t, trackSraEvent, hasActiveJwtvCampaign, sponsoredAd, isShadowCampaign, buyBoxActiveType, presentationTypeGraphql, queryVariables, loading, result, onResult, cachedResults, offers, hardcodedShortName, hasTitleBasedSteering, inPromotionCatalogue, catalogueItem, expGridSwiperVariant, expGridSwiperActive, isBingeAuPromoTitle, gridRedesignVariant, renderGridRedesign, isFreeBuyBox, excludedPackages, filteredOffers, filteredOffersFree, filteredOffersSorted, hasNoNonFreeOffers, hasOnlyCinemaOffers, showPromotionRow, titleContext, hasNoOffersToShow, lastUpdated, isTitleDetailPage, timestampText, sponsoredRecommendationPackageId, sponsoredRecommendationCohort, offerReporterTitleOffers, fastOffers, showFastRow, trackingContexts, hideTVODQTV, promotionProviderShortName, qualityTvActive, isSqtvActive, isSQTVEmpty, showSQTV, freeTrialActive, promotionShortName, offerRows, handleEpisodeFilterChange, setEpisodeBuyboxPresentationType, clickOut, getRentAndBuyOffers, SponsoredRecommendation, NewBuyBoxView, GridBuyBoxFilter, OfferRow, BuyBoxCountrySwitcher, GridRowLabel, GridSwiperExp }
}

})